// Bilingual public-site URL helpers.
//
// The public marketing pages are exposed at CLEAN paths so that crawlers and
// react-snap get per-locale static HTML with a real, indexable URL:
//   French (default):  https://cffagroup.ca/            https://cffagroup.ca/privacy
//   English (mirror):   https://cffagroup.ca/en          https://cffagroup.ca/en/privacy
//
// The app itself still uses HashRouter. Every PRIVATE / authenticated route
// (/#/login, /#/dashboard, /#/reset-password, /#/security-alert, ...) and every
// existing email deep link keeps its `#/...` hash form untouched — this module
// only ever reasons about the public marketing pages.

const DOMAIN = 'https://cffagroup.ca';

// Public inner routes that exist as clean paths. These mirror the HashRouter
// routes declared in App.js. A pathname that (after stripping an optional
// "/en" prefix) is not in this list is left alone (default home behaviour),
// which is what keeps private clean paths from being hijacked.
export const PUBLIC_PATHS = [
    '/',
    '/privacy',
    '/about',
    '/locations',
    '/join-our-team',
    '/opportunity',
    '/contact',
    '/404',
];

// Normalise a pathname: drop trailing slashes but always keep a leading "/".
const clean = (p) => {
    const s = (p || '/').replace(/\/+$/, '');
    return s === '' ? '/' : s;
};

// Parse a real pathname into { lang, route } where `route` is the FR-canonical
// inner path (e.g. "/privacy") and `lang` is 'en' | 'fr'.
export function parsePublicPath(pathname) {
    const p = clean(pathname);
    const isEn = p === '/en' || p.startsWith('/en/');
    const route = isEn ? clean(p.slice(3)) : p; // strip the "/en" prefix
    return { lang: isEn ? 'en' : 'fr', route };
}

// The active public locale, derived from the current URL (defaults to 'fr').
export function currentPublicLang() {
    if (typeof window === 'undefined') return 'fr';
    return parsePublicPath(window.location.pathname).lang;
}

// Same-origin path for a given locale + inner route (used for navigation).
export function publicPath(lang, route) {
    const r = clean(route);
    const prefix = lang === 'en' ? '/en' : '';
    if (r === '/') return prefix || '/';
    return `${prefix}${r}`;
}

// Absolute canonical URL for a given locale + inner route (used in meta tags).
export function publicUrl(lang, route) {
    return `${DOMAIN}${publicPath(lang, route)}`;
}

// Apply the URL-derived SEO head tags shared by every public page:
//   <html lang>, canonical, hreflang (fr / en / x-default), og:url, og:locale.
// Page-specific tags (title, description, og:title, ...) stay in the page
// components. Safe to call repeatedly — it upserts rather than duplicates.
export function applyPublicSeo(lang, route) {
    if (typeof document === 'undefined') return;
    const head = document.head;

    const upsertLink = (rel, href, hreflang) => {
        const sel = hreflang
            ? `link[rel="${rel}"][hreflang="${hreflang}"]`
            : `link[rel="${rel}"]:not([hreflang])`;
        let el = head.querySelector(sel);
        if (!el) {
            el = document.createElement('link');
            el.setAttribute('rel', rel);
            if (hreflang) el.setAttribute('hreflang', hreflang);
            head.appendChild(el);
        }
        el.setAttribute('href', href);
    };

    const setMeta = (selector, content) => {
        const el = head.querySelector(selector);
        if (el && content) el.setAttribute('content', content);
    };

    const canonical = publicUrl(lang, route);
    const frUrl = publicUrl('fr', route);
    const enUrl = publicUrl('en', route);

    document.documentElement.lang = lang;
    upsertLink('canonical', canonical);
    upsertLink('alternate', frUrl, 'fr');
    upsertLink('alternate', enUrl, 'en');
    upsertLink('alternate', frUrl, 'x-default'); // x-default -> French (default)

    setMeta('meta[property="og:url"]', canonical);
    setMeta('meta[property="og:locale"]', lang === 'en' ? 'en_CA' : 'fr_CA');
    setMeta('meta[property="og:locale:alternate"]', lang === 'en' ? 'fr_CA' : 'en_CA');
}
